import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import type { Ref } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { IntegrationConnection } from "@common/types/integrationConnection";
import {
    PlainModal,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { Entity } from "@common/types/entity";
import { validateFormikWithZodSchema } from "@helpers/form";
import { isBillcomSandbox } from "@helpers/billcom";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { useBillcomConnectionCreationMutation } from "@api/billcom.api";
import billcomPreview from "@static/images/previews/billcom-overview.svg";
import { CreateAccountHandle } from "../accounts/AddAccounts/lib";
import styles from "./CreateBillcomConnection.module.scss";

const BILLCOM_SANDBOX_TOKEN_SETUP_URL =
    "https://app-stage02.us.bill.com/neo/settings/sync-and-integrations/tokens";
const BILLCOM_PROD_TOKEN_SETUP_URL =
    "https://app02.us.bill.com/neo/settings/sync-and-integrations/tokens";

const BILLCOM_TOKEN_DOCS_URL =
    "https://docs.kick.co/accountants/setting-up/integrations/bill";

function getBillcomTokenSetupUrl(): string {
    if (isBillcomSandbox()) {
        return BILLCOM_SANDBOX_TOKEN_SETUP_URL;
    }
    return BILLCOM_PROD_TOKEN_SETUP_URL;
}

const createBillcomConnectionFormSchema = z.object({
    tokenName: z.string().trim().min(1, "Token name is required"),
    tokenValue: z.string().trim().min(1, "Token value is required"),
});

type CreateBillcomConnectionFormValues = z.infer<
    typeof createBillcomConnectionFormSchema
>;

const fields = createFormikFields<CreateBillcomConnectionFormValues>();

type BillcomConnectionScreen = "overview" | "form";

interface Props {
    children?:
        | React.ReactNode
        | ((props: {
              isConnecting: boolean;
              initializeConnection: () => void;
          }) => React.ReactNode);
    onCreated?: (connection: IntegrationConnection) => void;
    defaultEntity?: Entity;
}

export const CreateBillcomConnection: React.FC<
    Props & { ref?: Ref<CreateAccountHandle> }
> = ({ children, onCreated, defaultEntity, ref }) => {
    const [showModal, setShowModal] = useState(false);
    const [screen, setScreen] = useState<BillcomConnectionScreen>("overview");
    const [submittedValues, setSubmittedValues] =
        useState<CreateBillcomConnectionFormValues | null>(null);

    const handleSuccess = useCallback(
        (connection: IntegrationConnection) => {
            setShowModal(false);
            setScreen("overview");
            onCreated?.(connection);
        },
        [onCreated],
    );

    const mutation = useBillcomConnectionCreationMutation(handleSuccess);

    const formik = useFormik<CreateBillcomConnectionFormValues>({
        initialValues: {
            tokenName: "",
            tokenValue: "",
        },
        validateOnMount: true,
        validate: validateFormikWithZodSchema(
            createBillcomConnectionFormSchema,
        ),
        onSubmit: (values) => {
            const nextSubmittedValues = {
                tokenName: values.tokenName.trim(),
                tokenValue: values.tokenValue.trim(),
            };
            setSubmittedValues(nextSubmittedValues);
            mutation.mutate({
                ...nextSubmittedValues,
                defaultEntityId: defaultEntity?.id,
            });
        },
    });

    useEffect(() => {
        if (!mutation.isError || !submittedValues) {
            return;
        }
        if (
            submittedValues.tokenName === formik.values.tokenName &&
            submittedValues.tokenValue === formik.values.tokenValue
        ) {
            return;
        }
        mutation.reset();
        setSubmittedValues(null);
    }, [
        formik.values.tokenName,
        formik.values.tokenValue,
        mutation,
        submittedValues,
    ]);

    const initializeConnection = useCallback(() => {
        setShowModal(true);
        setScreen("overview");
        setSubmittedValues(null);
        formik.resetForm();
        mutation.reset();
    }, [formik, mutation]);

    useImperativeHandle(
        ref,
        () => ({
            initializeConnection,
        }),
        [initializeConnection],
    );

    const handleClose = useCallback(() => {
        if (!mutation.isPending) {
            setShowModal(false);
            setScreen("overview");
        }
    }, [mutation.isPending]);

    if (typeof children === "function") {
        return (
            <>
                {children({
                    isConnecting: mutation.isPending,
                    initializeConnection,
                })}
                <BillcomConnectionModal
                    show={showModal}
                    onHide={handleClose}
                    screen={screen}
                    onStartConnection={() => setScreen("form")}
                    formik={formik}
                    isPending={mutation.isPending}
                    isError={mutation.isError}
                />
            </>
        );
    }

    return (
        <>
            <Button
                variant="secondary"
                size="sm"
                onClick={initializeConnection}
            >
                {children ?? "Connect"}
            </Button>
            <BillcomConnectionModal
                show={showModal}
                onHide={handleClose}
                screen={screen}
                onStartConnection={() => setScreen("form")}
                formik={formik}
                isPending={mutation.isPending}
                isError={mutation.isError}
            />
        </>
    );
};

interface ModalProps {
    show: boolean;
    onHide: () => void;
    screen: BillcomConnectionScreen;
    onStartConnection: () => void;
    formik: ReturnType<typeof useFormik<CreateBillcomConnectionFormValues>>;
    isPending: boolean;
    isError: boolean;
}

const BillcomConnectionModal: React.FC<ModalProps> = ({
    show,
    onHide,
    screen,
    onStartConnection,
    formik,
    isPending,
    isError,
}) => {
    const isOverviewScreen = screen === "overview";
    const canSubmit =
        formik.values.tokenName.trim().length > 0 &&
        formik.values.tokenValue.trim().length > 0 &&
        formik.isValid;

    return (
        <PlainModal show={show} onHide={onHide} size="lg">
            {isOverviewScreen ? (
                <div className={styles.overview}>
                    <StandardModalHeader>
                        Connect your BILL account
                    </StandardModalHeader>

                    <div className={styles.overviewCopy}>
                        <p>
                            Automate BILL bill and invoice sync in Kick. Connect
                            your account in a few steps.
                        </p>
                        {isBillcomSandbox() && (
                            <div className={styles.environmentNotice}>
                                You&apos;re connecting to the BILL sandbox. Use
                                a sandbox AP &amp; AR sync token.
                            </div>
                        )}
                    </div>

                    <div className={styles.preview}>
                        <div className={styles.previewImageFrame}>
                            <img
                                src={billcomPreview}
                                alt="BILL bills preview"
                                className={styles.previewImage}
                            />
                        </div>
                    </div>

                    <div className={styles.overviewActions}>
                        <Button variant="secondary" size="xl" asChild>
                            <a
                                href={getBillcomTokenSetupUrl()}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open token settings
                            </a>
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            size="xl"
                            onClick={onStartConnection}
                        >
                            Connect account
                        </Button>
                    </div>
                </div>
            ) : (
                <FormikProvider value={formik}>
                    <Form className={styles.form}>
                        <StandardModalHeader>
                            Connect your BILL account
                        </StandardModalHeader>

                        <div className={styles.formIntro}>
                            <p>
                                Paste the AP &amp; AR sync token from BILL to
                                finish connecting your account.
                            </p>
                        </div>

                        <div className={styles.formActions}>
                            <Button variant="secondary" size="sm" asChild>
                                <a
                                    href={getBillcomTokenSetupUrl()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Open token settings
                                </a>
                            </Button>
                            <Button variant="secondary" size="sm" asChild>
                                <a
                                    href={BILLCOM_TOKEN_DOCS_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View setup guide
                                </a>
                            </Button>
                        </div>

                        <div className={styles.fields}>
                            <fields.Text
                                name="tokenName"
                                label="Token name"
                                placeholder="e.g. Kick AP & AR token"
                                disabled={isPending}
                                required
                                size="xl"
                                className={styles.textField}
                            />

                            <fields.Text
                                name="tokenValue"
                                type="password"
                                label="Token value"
                                placeholder="Paste your BILL token"
                                disabled={isPending}
                                required
                                size="xl"
                                className={styles.textField}
                            />
                        </div>

                        {isError && (
                            <div className={styles.error}>
                                BILL couldn&apos;t verify this token. Check the
                                token name and value, then try again.
                            </div>
                        )}

                        <StandardModalFooter className={styles.footer}>
                            <Button
                                type="button"
                                variant="secondary"
                                size="md"
                                onClick={onHide}
                                disabled={isPending}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                type="submit"
                                variant="primary"
                                size="md"
                                loading={isPending}
                                disabled={!canSubmit}
                            >
                                Connect account
                            </ButtonWithLoader>
                        </StandardModalFooter>
                    </Form>
                </FormikProvider>
            )}
        </PlainModal>
    );
};
